import {UseMutationOptions} from '@tanstack/react-query';
import {postApiStoreCommunitySetMyrating} from '../api/store/postApiStoreCommunitySetMyrating';
import {postStoreCommunityBlock} from '../api/store/postStoreCommunityBlock';
import {postStoreCommunityDeclare} from '../api/store/postStoreCommunityDeclare';
import {postStoreCommunityLike} from '../api/store/postStoreCommunityLike';
import {postStoreCommunityRemoveComment} from '../api/store/postStoreCommunityRemoveComment';
import {postStoreCommunityWriteComment} from '../api/store/postStoreCommunityWriteComment';
import {postStoreCouponHistoryRefund} from '../api/store/postStoreCouponHistoryRefund';
import {postStoreReceiptDelete} from '../api/store/postStoreReceiptDelete';
import {postStoreReceiptInfo} from '../api/store/postStoreReceiptInfo';
import {postStoreReceiptUpdate} from '../api/store/postStoreReceiptUpdate';
import {postStoreRedeemCodeUse} from '../api/store/postStoreRedeemCodeUse';
import {postStoreRemoveMyNews} from '../api/store/postStoreRemoveMyNews';
import {postStoreSetSeriesAlarm} from '../api/store/postStoreSetSeriesAlarm';
import {postStoreSetSeriesLike} from '../api/store/postStoreSetSeriesLike';
import {postStoreTermsSetTvPoint} from '../api/store/postStoreTermsSetTvPoint';
import {postStoreUpdateNickname} from '../api/store/postStoreUpdateNickname';
import {AgentCode} from '../common/AgentCode.enum';
import {requestContext} from '../common/requestContext';
import {getSerializedPagewebError} from '../errors/getSerializedPagewebError.function';
import {PagewebError} from '../errors/PagewebError.class';
import {createReceiptInfo} from '../graphql/factory/ReceiptInfo';
import {createRedeemCode} from '../graphql/factory/RedeemCode';
import {createNicknameInfo} from '../graphql/factory/UserProfile';
import {
  BlockUserMutation,
  type BlockUserMutationVariables,
  DeclareCommentMutation,
  type DeclareCommentMutationVariables,
  DeleteReceiptInfoMutation,
  DeleteReceiptInfoMutationVariables,
  LikeCommentMutation,
  type LikeCommentMutationVariables,
  type RefundTicketMutation,
  type RefundTicketMutationVariables,
  type RemoveCommentMutation,
  type RemoveCommentMutationVariables,
  type RemoveMyNewsItemMutation,
  type RemoveMyNewsItemMutationVariables,
  ResultStatus,
  SetMyRatingMutation,
  SetMyRatingMutationVariables,
  SetSeriesAlarmMutation,
  SetSeriesAlarmMutationVariables,
  SetSeriesLikeMutation,
  SetSeriesLikeMutationVariables,
  SetTvPointAgreemenMutation,
  SetTvPointAgreemenMutationVariables,
  UpdateNicknameMutation,
  UpdateNicknameMutationVariables,
  UpdateReceiptInfoMutation,
  UpdateReceiptInfoMutationVariables,
  UseRedeemMutation,
  UseRedeemMutationVariables,
  WriteCommentMutation,
  WriteCommentMutationVariables,
} from '../graphql/generated/types';

/**Mutation Adapter
 */
export const storeMutationAdapter = {
  /**
   * 사용자 차단
   *
   * ⚠️ Resolver 로직:
   * - try-catch 없음 (에러 발생 시 throw)
   * - 성공하면 {status: ResultStatus.Success} 반환
   * - React Query의 error state 사용
   *
   * 사용법:
   * ```tsx
   * const mutation = useMutation(storeMutationAdapter.blockUser());
   * mutation.mutate({blockUserInput: {userUid: 123, state: true}});
   * // 성공: mutation.isSuccess
   * // 실패: mutation.isError
   * ```
   */
  blockUser: (): UseMutationOptions<
    BlockUserMutation,
    PagewebError,
    BlockUserMutationVariables
  > => ({
    mutationFn: async (variables: BlockUserMutationVariables) => {
      await postStoreCommunityBlock(variables.blockUserInput);
      return {
        blockUser: {
          status: ResultStatus.Success,
        },
      };
    },
  }),

  /**
   * 댓글 신고
   *
   * ⚠️ Resolver 로직:
   * - try-catch로 감싸서 항상 성공 반환
   * - 성공: {status: ResultStatus.Success, landingUrl: result.landing_url}
   * - 실패: {status: ResultStatus.Fail}
   * - isError는 항상 false
   *
   * 사용법:
   * ```tsx
   * const mutation = useMutation(storeMutationAdapter.declareComment());
   * mutation.mutate({declareCommentInput: {commentUid: '123', declareType: 'A'}});
   * // 성공/실패 확인: mutation.data.status === ResultStatus.Success
   * ```
   */
  declareComment: (): UseMutationOptions<
    DeclareCommentMutation,
    PagewebError,
    DeclareCommentMutationVariables
  > => ({
    mutationFn: async (variables: DeclareCommentMutationVariables) => {
      try {
        const result = await postStoreCommunityDeclare(
          variables.declareCommentInput
        );
        return {
          declareComment: {
            status: ResultStatus.Success,
            landingUrl: result.landing_url,
          },
        };
      } catch (_e) {
        return {
          declareComment: {
            status: ResultStatus.Fail,
          },
        };
      }
    },
  }),
  /**
   * 시리즈 알림 설정
   *
   * ⚠️ Resolver 로직:
   * - try-catch 없음 (에러 발생 시 throw)
   * - 성공 시 {record, recordId, status: ResultStatus.Success} 반환
   * - Factory 함수 없음
   */
  setSeriesAlarm: (): UseMutationOptions<
    SetSeriesAlarmMutation,
    PagewebError,
    SetSeriesAlarmMutationVariables
  > => ({
    mutationFn: async (variables: SetSeriesAlarmMutationVariables) => {
      const {setSeriesAlarmInput} = variables;

      await postStoreSetSeriesAlarm({
        series_id: setSeriesAlarmInput.seriesId,
        state: setSeriesAlarmInput.isAlarmOn,
      });

      return {
        setSeriesAlarm: {
          record: setSeriesAlarmInput.isAlarmOn,
          recordId: String(setSeriesAlarmInput.seriesId),
          status: ResultStatus.Success,
        },
      };
    },
  }),

  /**
   * 시리즈 좋아요 설정
   *
   * ⚠️ Resolver 로직:
   * - try-catch 없음 (에러 발생 시 throw)
   * - 성공 시 {record, recordId, status: ResultStatus.Success} 반환
   * - Factory 함수 없음
   */
  setSeriesLike: (): UseMutationOptions<
    SetSeriesLikeMutation,
    PagewebError,
    SetSeriesLikeMutationVariables
  > => ({
    mutationFn: async (variables: SetSeriesLikeMutationVariables) => {
      const {setSeriesLikeInput} = variables;

      await postStoreSetSeriesLike({
        series_id: setSeriesLikeInput.seriesId,
        state: setSeriesLikeInput.isLike,
      });

      return {
        setSeriesLike: {
          record: setSeriesLikeInput.isLike,
          recordId: String(setSeriesLikeInput.seriesId),
          status: ResultStatus.Success,
        },
      };
    },
  }),

  /**
   * 댓글 작성
   *
   * ⚠️ Resolver 로직:
   * - response.result_code >= 0이면 Success, 아니면 Fail
   * - 실패 시 user_comment_block_msg를 blockMsg로 전달
   */
  writeComment: (): UseMutationOptions<
    WriteCommentMutation,
    PagewebError,
    WriteCommentMutationVariables
  > => ({
    mutationFn: async (variables: WriteCommentMutationVariables) => {
      const response = await postStoreCommunityWriteComment(
        variables.writeCommentInput
      );

      if (response.result_code >= 0) {
        return {
          writeComment: {
            status: ResultStatus.Success,
          },
        };
      }

      return {
        writeComment: {
          status: ResultStatus.Fail,
          blockMsg: response.user_comment_block_msg,
        },
      };
    },
  }),

  /**
   * 댓글 삭제
   *
   * ⚠️ Resolver 로직: 에러 발생 throw, 성공 시 Success 고정 반환
   */
  removeComment: (): UseMutationOptions<
    RemoveCommentMutation,
    PagewebError,
    RemoveCommentMutationVariables
  > => ({
    mutationFn: async (variables: RemoveCommentMutationVariables) => {
      await postStoreCommunityRemoveComment(variables.commentUid);
      return {
        removeComment: {
          status: ResultStatus.Success,
        },
      };
    },
  }),

  /**
   * 리딤 코드 사용
   *
   * ⚠️ Resolver 로직:
   * - pageNicolasApi.getUseRedeemCode 호출 시 성공 응답은 createRedeemCode로 변환
   * - 실패 시에도 에러를 삼키고 result_code, title 기반으로 createRedeemCode 생성
   */
  useRedeem: (): UseMutationOptions<
    UseRedeemMutation,
    PagewebError,
    UseRedeemMutationVariables
  > => ({
    mutationFn: async (variables: UseRedeemMutationVariables) => {
      const {code, agentCode, confirmed} = variables.input;

      try {
        const response = await postStoreRedeemCodeUse({
          code,
          agentCode: agentCode as AgentCode,
          confirmed,
        });

        return {useRedeem: createRedeemCode(response)};
      } catch (error) {
        const pagewebError = getSerializedPagewebError(error);
        return {
          useRedeem: createRedeemCode({
            result_code: pagewebError.resultCode,
            title: pagewebError.message,
          }),
        };
      }
    },
  }),

  /**
   * 닉네임 변경
   *
   * ⚠️ Resolver 로직:
   * - 서버 컨텍스트에서 kakaoAuth.accessToken을 사용했지만, BFF가 세션/쿠키 기반으로 인증을 부착하므로
   *   클라이언트에서 Authorization 헤더를 직접 전달할 필요가 없습니다.
   * - Adapter는 fetcher 결과를 GraphQL Payload 셰이프로 변환하여 반환합니다.
   */
  updateNickname: (): UseMutationOptions<
    UpdateNicknameMutation,
    PagewebError,
    UpdateNicknameMutationVariables
  > => ({
    mutationFn: async (variables: UpdateNicknameMutationVariables) => {
      const nickname = variables.input.nickname;
      const {kakaoAuth} = requestContext.requestInfo;

      if (!kakaoAuth || !kakaoAuth.accessToken) {
        throw new Error('인증 정보가 없습니다');
      }

      const result = await postStoreUpdateNickname({nickname});

      return {updateNickname: createNicknameInfo(result)};
    },
  }),

  /**
   * 영수증 정보 삭제
   *
   * ⚠️ Resolver 로직:
   * - try-catch로 감싸서 항상 성공 반환
   * - 성공: {status: ResultStatus.Success}
   * - 실패: {status: ResultStatus.Fail}
   * - isError는 항상 false
   * - 파라미터 없음
   *
   * 사용법:
   * ```tsx
   * const mutation = useMutation(storeMutationAdapter.deleteReceiptInfo());
   * mutation.mutate();
   * // 성공/실패 확인: mutation.data.status === ResultStatus.Success
   * ```
   */
  deleteReceiptInfo: (): UseMutationOptions<
    DeleteReceiptInfoMutation,
    PagewebError,
    DeleteReceiptInfoMutationVariables
  > => ({
    mutationFn: async () => {
      try {
        await postStoreReceiptDelete();
        return {
          deleteReceiptInfo: {
            status: ResultStatus.Success,
          },
        };
      } catch (_e) {
        return {
          deleteReceiptInfo: {
            status: ResultStatus.Fail,
          },
        };
      }
    },
  }),

  setMyRating: (): UseMutationOptions<
    SetMyRatingMutation,
    PagewebError,
    SetMyRatingMutationVariables
  > => ({
    mutationFn: async (variables: SetMyRatingMutationVariables) => {
      const {seriesId, productId, rating} = variables.setMyRatingInput;
      await postApiStoreCommunitySetMyrating({
        series_id: seriesId,
        product_id: productId,
        rating,
      });
      return {
        setMyRating: {
          status: ResultStatus.Success,
        },
      };
    },
  }),
  /**
   * 영수증 정보 변경
   *
   * ⚠️ Resolver 로직:
   * - updateReceiptInfo 호출 후 falsy 응답이면 에러 throw
   * - 이후 getReceiptInfo로 최신 데이터를 조회
   * - response.data 없으면 필드를 undefined로 채운 객체 반환
   */
  updateReceiptInfo: (): UseMutationOptions<
    UpdateReceiptInfoMutation,
    PagewebError,
    UpdateReceiptInfoMutationVariables
  > => ({
    mutationFn: async (variables: UpdateReceiptInfoMutationVariables) => {
      const {receiptInfoType, receiptInfo} = variables.input;

      const updateResponse = await postStoreReceiptUpdate({
        receiptInfoType,
        receiptInfo,
      });

      if (!updateResponse) {
        throw new Error('현금영수증 정보 변경이 실패했습니다.');
      }

      const response = await postStoreReceiptInfo();

      if (response.data) {
        return {updateReceiptInfo: createReceiptInfo(response.data)};
      }

      return {
        updateReceiptInfo: {
          accountId: undefined,
          receiptInfoType: undefined,
          receiptInfo: undefined,
        },
      };
    },
  }),

  /**

   * 이용권 환불
   *
   * ⚠️ Resolver 로직: 에러 발생 시 throw, 성공 시 Success 고정 반환
   */
  refundTicket: (): UseMutationOptions<
    RefundTicketMutation,
    PagewebError,
    RefundTicketMutationVariables
  > => ({
    mutationFn: async (variables: RefundTicketMutationVariables) => {
      await postStoreCouponHistoryRefund({ticketUid: variables.ticketUid});
      return {
        refundTicket: {
          status: ResultStatus.Success,
        },
      };
    },
  }),

  /**
   * 내 소식 항목 삭제
   *
   * ⚠️ Resolver 로직: 성공 시 newsId 반환
   */
  removeMyNews: (): UseMutationOptions<
    RemoveMyNewsItemMutation,
    PagewebError,
    RemoveMyNewsItemMutationVariables
  > => ({
    mutationFn: async (variables: RemoveMyNewsItemMutationVariables) => {
      const {newsId} = variables.input;
      await postStoreRemoveMyNews({newsId});
      return {removeMyNews: {newsId}};
    },
  }),

  /**
   * 댓글 좋아요/좋아요 취소
   *
   * ⚠️ Resolver 로직 (완전 복제됨):
   * - try-catch 없음 (에러 발생 시 throw)
   * - 성공하면 {status: ResultStatus.Success, likeCount: response.like_count} 반환
   * - React Query error state 사용
   * - Factory 함수 없음
   *
   * 사용법:
   * ```tsx
   * const mutation = useMutation(storeMutationAdapter.likeComment());
   * mutation.mutate({likeCommentInput: {commentUid: 123, like: true}});
   * // 성공: mutation.data.likeCount
   * // 실패: mutation.isError
   * ```
   */
  likeComment: (): UseMutationOptions<
    LikeCommentMutation,
    PagewebError,
    LikeCommentMutationVariables
  > => ({
    mutationFn: async (variables: LikeCommentMutationVariables) => {
      const response = await postStoreCommunityLike(variables.likeCommentInput);
      return {
        likeComment: {
          status: ResultStatus.Success,
          likeCount: response.like_count,
        },
      };
    },
  }),

  /**
   * TV포인트 약관 동의
   *
   * ⚠️ Resolver 로직 (완전 복제됨):
   * - try-catch 없음 (에러 발생 시 throw)
   * - 성공하면 {status: ResultStatus.Success} 반환
   * - React Query error state 사용
   *
   * 사용법:
   * ```tsx
   * const mutation = useMutation(storeMutationAdapter.setTvPointAgreement());
   * mutation.mutate({isAgree: true});
   * // 성공: mutation.isSuccess
   * // 실패: mutation.isError
   * ```
   */
  setTvPointAgreement: (): UseMutationOptions<
    SetTvPointAgreemenMutation,
    PagewebError,
    SetTvPointAgreemenMutationVariables
  > => ({
    mutationFn: async (variables: SetTvPointAgreemenMutationVariables) => {
      await postStoreTermsSetTvPoint({
        agree: variables.isAgree ? 'Y' : 'N',
      });

      return {
        setTvPointAgreement: {
          status: ResultStatus.Success,
        },
      };
    },
  }),
} as const;
